import { parseDay } from '../../utils/dateTypes';
import { DaySequenceBinaryProps } from './types';

/**
 * Calculates total days between start and end date (inclusive)
 */
export const calculateTotalDays = (startDate: DaySequenceBinaryProps['startDate'], endDate: DaySequenceBinaryProps['endDate']): number => {
  const start = parseDay(startDate);
  const end = parseDay(endDate);
  return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
};

/**
 * Generates array of all Date objects between start and end dates (inclusive)
 */
export const generateDateRange = (startDate: DaySequenceBinaryProps['startDate'], endDate: DaySequenceBinaryProps['endDate']): Date[] => {
  const start = parseDay(startDate);
  const end = parseDay(endDate);

  const allDates: Date[] = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    allDates.push(new Date(d));
  }

  return allDates;
};

/**
 * Creates a Set of event dates as date strings for quick lookup
 */
export const createEventLookupSet = (eventDates: DaySequenceBinaryProps['eventDates']): Set<string> => {
  return new Set(eventDates.map(d => parseDay(d).toDateString()));
};

/**
 * Checks if a date has an event
 */
export const hasEvent = (date: Date, eventSet: Set<string>): boolean => {
  return eventSet.has(date.toDateString());
};

/**
 * Checks if previous day has an event
 */
export const hasPrevEvent = (index: number, allDates: Date[], eventSet: Set<string>): boolean => {
  return index > 0 && hasEvent(allDates[index - 1], eventSet);
};

/**
 * Checks if next day has an event
 */
export const hasNextEvent = (index: number, allDates: Date[], eventSet: Set<string>): boolean => {
  return index < allDates.length - 1 && hasEvent(allDates[index + 1], eventSet);
};

/**
 * Checks if this is a week boundary (every 7th day from start)
 */
export const isWeekBoundary = (index: number, showWeekTicks: boolean): boolean => {
  return showWeekTicks && index > 0 && index % 7 === 0;
};

/**
 * Calculates border radius for a dot based on adjacent events
 */
export const getBorderRadius = (
  index: number,
  allDates: Date[],
  eventSet: Set<string>,
  borderRadius: number
): string => {
  if (!hasEvent(allDates[index], eventSet)) return '0';

  const prev = hasPrevEvent(index, allDates, eventSet);
  const next = hasNextEvent(index, allDates, eventSet);
  const radius = `${borderRadius}px`;

  if (!prev && !next) return radius;
  if (prev && next) return '0';
  if (prev && !next) return `0 ${radius} ${radius} 0`;
  if (!prev && next) return `${radius} 0 0 ${radius}`;

  return radius;
};

/**
 * Calculates dot width as percentage based on total days
 */
export const calculateDotWidth = (totalDays: number): number => {
  return Math.max(100 / totalDays, 0.5);
};